import React, { createContext, useContext, useState, useEffect } from 'react';
import { collection, query, where, onSnapshot, addDoc, updateDoc, doc, deleteDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { Integration, CronJob } from '../types/integrations';
import { useAuth } from './AuthContext';

interface IntegrationsContextData {
  integrations: Integration[];
  cronJobs: CronJob[];
  addIntegration: (integration: Omit<Integration, 'id'>) => Promise<string>;
  updateIntegration: (id: string, data: Partial<Integration>) => Promise<void>;
  deleteIntegration: (id: string) => Promise<void>;
  toggleIntegration: (id: string, active: boolean) => Promise<void>;
  loading: boolean;
  error: string | null;
}

const IntegrationsContext = createContext<IntegrationsContextData>({} as IntegrationsContextData);

export function IntegrationsProvider({ children }: { children: React.ReactNode }) {
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [cronJobs, setCronJobs] = useState<CronJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { currentUser } = useAuth();

  useEffect(() => {
    if (!currentUser?.companyId) return;

    const integrationsRef = collection(db, 'integrations');
    const integrationsQuery = query(
      integrationsRef,
      where('companyId', '==', currentUser.companyId)
    );

    const unsubscribe = onSnapshot(integrationsQuery, (snapshot) => {
      const integrationsData: Integration[] = [];
      snapshot.forEach((doc) => {
        integrationsData.push({ id: doc.id, ...doc.data() } as Integration);
      });
      setIntegrations(integrationsData);
      setLoading(false);
    }, (err) => {
      console.error('Error loading integrations:', err);
      setError('Erro ao carregar integrações');
      setLoading(false);
    });

    return () => unsubscribe();
  }, [currentUser?.companyId]);

  useEffect(() => {
    if (!currentUser?.companyId) return;

    const cronJobsRef = collection(db, 'cronJobs');
    const cronJobsQuery = query(
      cronJobsRef,
      where('companyId', '==', currentUser.companyId)
    );

    const unsubscribe = onSnapshot(cronJobsQuery, (snapshot) => {
      const cronJobsData: CronJob[] = [];
      snapshot.forEach((doc) => {
        cronJobsData.push({ id: doc.id, ...doc.data() } as CronJob);
      });
      setCronJobs(cronJobsData);
    }, (err) => {
      console.error('Error loading cron jobs:', err);
    });

    return () => unsubscribe();
  }, [currentUser?.companyId]);

  const addIntegration = async (integration: Omit<Integration, 'id'>) => {
    try {
      const integrationsRef = collection(db, 'integrations');
      const docRef = await addDoc(integrationsRef, {
        ...integration,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
      return docRef.id;
    } catch (err) {
      console.error('Error adding integration:', err);
      throw new Error('Erro ao adicionar integração');
    }
  };

  const updateIntegration = async (id: string, data: Partial<Integration>) => {
    try {
      const integrationRef = doc(db, 'integrations', id);
      await updateDoc(integrationRef, {
        ...data,
        updatedAt: new Date().toISOString()
      });
    } catch (err) {
      console.error('Error updating integration:', err);
      throw new Error('Erro ao atualizar integração');
    }
  };

  const deleteIntegration = async (id: string) => {
    try {
      const integrationRef = doc(db, 'integrations', id);
      await deleteDoc(integrationRef);
    } catch (err) {
      console.error('Error deleting integration:', err);
      throw new Error('Erro ao excluir integração');
    }
  };

  const toggleIntegration = async (id: string, active: boolean) => {
    await updateIntegration(id, { active });
  };

  return (
    <IntegrationsContext.Provider
      value={{
        integrations,
        cronJobs,
        addIntegration,
        updateIntegration,
        deleteIntegration,
        toggleIntegration,
        loading,
        error
      }}
    >
      {children}
    </IntegrationsContext.Provider>
  );
}

export function useIntegrations() {
  const context = useContext(IntegrationsContext);
  if (!context) {
    throw new Error('useIntegrations must be used within an IntegrationsProvider');
  }
  return context;
}
